import React, { useEffect, useRef, useState } from "react";
import { Button, LinearProgress, TextField } from "@material-ui/core";
import { Typography } from "@welldigital/components";
import {
  cancelOrderSubscription,
  CancelOrderSubscriptionPayload,
} from "pages/ClinicalReviewPage/ClinicalReviewApi";
import CustomDialog, {
  CustomDialogRef,
} from "pages/ClinicalReviewPage/components/CustomDialog";
import { useCurrentUserEmail } from "services/auth-hooks";
import "./subscription.scss";

interface PropsTypes {
  onClose: () => void;
  onSuccess: (subscriptionId: string) => void;
  productTitle?: string;
  subscriptionId: string;
}

const CancelSubscriptionDialog = ({
  onClose,
  onSuccess,
  productTitle,
  subscriptionId,
}: PropsTypes) => {
  const dialogRef = useRef<CustomDialogRef>(null);
  const isMountedRef = useRef(true);
  const email = useCurrentUserEmail();
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    dialogRef.current?.show();

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const trimmedReason = reason.trim();

  const getErrorMessage = (error: any) => {
    return (
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Error cancelling subscription"
    );
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!trimmedReason) {
      return;
    }

    setLoading(true);
    setErrorMessage("");

    const payload: CancelOrderSubscriptionPayload = {
      updatedBy: email || "test",
      reason: trimmedReason,
    };

    try {
      const response = await cancelOrderSubscription(subscriptionId, payload);

      if (response?.status >= 200 && response?.status < 300) {
        dialogRef.current?.hide();
        onSuccess(subscriptionId);
        return;
      }

      if (isMountedRef.current) {
        setErrorMessage(
          `Unable to cancel subscription (${response?.status ?? "unknown"}).`,
        );
      }
    } catch (error) {
      if (isMountedRef.current) {
        setErrorMessage(getErrorMessage(error));
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  return (
    <CustomDialog
      onClose={onClose}
      ref={dialogRef}
      title={
        <>
          Cancel subscription
          {loading && (
            <div className="mt-20 mb-20">
              <LinearProgress />
            </div>
          )}
        </>
      }
      className="cancel-subscription-dialog"
    >
      <form onSubmit={handleSubmit}>
        <div className="cancel-subscription-container">
          <Typography className="popup-para-color mb-10">
            This will cancel subscription <b>{subscriptionId}</b> for{" "}
            <b>{productTitle || "this product"}</b>.
          </Typography>
          <Typography className="popup-para-color mb-10">
            Add a reason before confirming. The reason will be stored as an
            order note for audit.
          </Typography>
          <Typography className="font-weight-700 color-primary-hale mb-10">
            Reason
          </Typography>
          <TextField
            className="input cancel-subscription-reason"
            multiline
            variant="outlined"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            minRows={4}
            maxRows={4}
            placeholder="Add the cancellation reason"
          />
          {!!errorMessage && (
            <Typography variant="subtitle2" className="color-error">
              {errorMessage}
            </Typography>
          )}
          <div className="buttons-container cancel-subscription-actions">
            <Button
              variant="outlined"
              className="button-secondary"
              onClick={() => dialogRef.current?.hide()}
              disabled={loading}
            >
              Keep subscription
            </Button>
            <Button
              type="submit"
              variant="contained"
              className="cancel-subscription-confirmButton"
              disabled={!trimmedReason || loading}
            >
              Cancel subscription
            </Button>
          </div>
        </div>
      </form>
    </CustomDialog>
  );
};

export default CancelSubscriptionDialog;
